import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicHeader, ArabicFooter } from "./ArabicHome";
import SmsDonationOptions from "../component/SmsDonationOptions";

const CART_STORAGE_KEY = "tarahumDonationCart";

const presetAmounts = [100, 500, 2500, 10000];
const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US");

const localizedValue = (item, field, language, fallback = "") => language === "ar" ? item?.[`${field}_ar`] || item?.[field] || item?.[`${field}_en`] || fallback : item?.[`${field}_en`] || item?.[field] || item?.[`${field}_ar`] || fallback;

const mapQuickContent = (item, language = "en") => ({
  id: item.id || "", title: stripHtml(localizedValue(item, "title", language)), subtitle: stripHtml(localizedValue(item, "subtitle", language)),
  image: getCloudflareImageUrl(item.ImagePath || item.image, "w=1200,sharpen=1"),
  stats: [1, 2, 3].map((number) => ({ title: stripHtml(localizedValue(item, `box${number}title`, language)), subtitle: stripHtml(localizedValue(item, `box${number}subtitle`, language)) })).filter((stat) => stat.title || stat.subtitle),
  bottomTitle: stripHtml(localizedValue(item, "bottomtitle", language)), bottomSubtitle: stripHtml(localizedValue(item, "bottomsubtitle", language)),
});

const mapDonationCategory = (item, index, language = "en") => {
  const position = parseAmount(item.position || index + 1);
  const title = stripHtml(localizedValue(item, "title", language));
  return {
    id: item.id || `donation-category-${index}`, title, subtitle: stripHtml(localizedValue(item, "subtitle", language)),
    image: getCloudflareImageUrl(item.ImagePath || item.image || item.icon, "w=180,sharpen=1"), position,
    type: position === 4 ? "critical-case" : position === 3 ? "project" : "quick-donation", typeLabel: title, isStatic: false,
  };
};

const mapDonationOption = (item, index, language = "en") => ({
  id: item.id || `donation-option-${index}`,
  catid: item.catid || item.id || "0",
  projectid: item.id || "0",
  title: stripHtml(localizedValue(item, "title", language)),
  categoryTitle: stripHtml(localizedValue(item, "category_title", language)) || "",
  amount: parseAmount(item.amount),
});

const getStoredCartItems = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const items = stored ? JSON.parse(stored) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const navigateTo = (path) => {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new Event("app:navigate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export default function QuickDonation({ language = "en" }) {
  const interactions = useHomeInteractions();
  const text = language === "ar" ? {
    quickDonation: "التبرع السريع", secure: "آمن وموثوق", dataSafe: "بياناتك آمنة معنا", chooseWhere: "1. اختر جهة التبرع", selectInitiative: "اختر البرنامج / المبادرة", chooseAmount: "2. اختر مبلغ التبرع", otherAmount: "مبلغ آخر", secureNote: "جميع المعاملات آمنة ومشفرة وتلتزم بأعلى معايير الأمان.", donateNow: "تبرع الآن", addToCart: "أضف إلى السلة", otherWays: "طرق أخرى للتبرع", code: "الرمز", currency: "درهم"
  } : {
    quickDonation: "Quick Donation", secure: "Secure & Trusted", dataSafe: "Your data is safe with us", chooseWhere: "1. Choose Where You Want To Donate", selectInitiative: "Select Program / Initiative", chooseAmount: "2. Choose Amount", otherAmount: "Other Amount", secureNote: "All transactions are secure, encrypted, and comply with the highest security standards.", donateNow: "Donate Now", addToCart: "Add to Cart", otherWays: "Other Ways to Donate", code: "Code", currency: "AED"
  };
  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [language]);
  const [content, setContent] = useState({ stats: [] });
  const [donationCategories, setDonationCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [initiativeOptions, setInitiativeOptions] = useState([]);
  const [selectedAmount, setSelectedAmount] = useState(500);
  const [customAmount, setCustomAmount] = useState("");
  const [selectedDropdownId, setSelectedDropdownId] = useState("");
  const [selectedInitiativeId, setSelectedInitiativeId] = useState("");
  const [validationMessage, setValidationMessage] = useState("");

  const selectedCategory = useMemo(
    () => donationCategories.find((category) => String(category.id) === String(selectedCategoryId)) || donationCategories[0],
    [donationCategories, selectedCategoryId]
  );
  const selectedDropdownOption = useMemo(
    () => dropdownOptions.find((option) => String(option.id) === String(selectedDropdownId)) || null,
    [dropdownOptions, selectedDropdownId]
  );
  const selectedInitiative = useMemo(
    () => initiativeOptions.find((option) => String(option.id) === String(selectedInitiativeId)) || null,
    [initiativeOptions, selectedInitiativeId]
  );

  const donationAmount = customAmount ? parseAmount(customAmount) : selectedAmount;
  const selectedPosition = parseAmount(selectedCategory?.position);
  const showProjectDropdown = selectedPosition === 3 || selectedPosition === 4;
  const showInitiativeDropdown = Boolean(selectedCategory?.isStatic);
  const dropdownLabel = selectedPosition === 4 ? "Select Case" : "Select Project";
  const leftPanelStyle = content.image
    ? {
        backgroundImage: `url("${content.image}")`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
      }
    : undefined;

  useEffect(() => {
    document.title = "Quick Donation | Tarahum Charity Foundation";
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadQuickDonation = async () => {
      try {
        const response = await fetch(getApiUrl("/api/GetQuickDonationMainList.ashx"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Quick donation request failed with ${response.status}`);

        const result = await response.json();
        const firstRow = Array.isArray(result?.data) ? result.data[0] : null;
        if (firstRow) setContent(mapQuickContent(firstRow, language));
      } catch (error) {
        if (error.name !== "AbortError") console.warn("Unable to load quick donation content", error);
      }
    };

    loadQuickDonation();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadDonationCategories = async () => {
      try {
        const response = await fetch(getApiUrl("/api/GetDonationCategoryList.ashx?typ=Quick-Donation&pageSize=50"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Donation category request failed with ${response.status}`);

        const result = await response.json();
        const apiCategories = (Array.isArray(result?.data) ? result.data : [])
          .map((item, index) => mapDonationCategory(item, index, language))
          .filter((category) => category.title)
          .slice(0, 4);
        const nextCategories = apiCategories;

        setDonationCategories(nextCategories);
        setSelectedCategoryId(nextCategories[0]?.id || "");
      } catch (error) {
        if (error.name !== "AbortError") console.warn("Unable to load quick donation categories", error);
      }
    };

    loadDonationCategories();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setSelectedDropdownId("");
    setSelectedInitiativeId("");
    setDropdownOptions([]);

    if (!showProjectDropdown || !selectedCategory?.id) return undefined;

    const controller = new AbortController();

    const loadDonationOptions = async () => {
      try {
        const response = await fetch(
          getApiUrl(`/api/GetDonationProjectList.ashx?catid=${encodeURIComponent(selectedCategory.id)}`),
          {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: controller.signal,
          }
        );

        if (!response.ok) throw new Error(`Donation option request failed with ${response.status}`);

        const result = await response.json();
        setDropdownOptions((Array.isArray(result?.data) ? result.data : []).map((item, index) => mapDonationOption(item, index, language)).filter((option) => option.title));
      } catch (error) {
        if (error.name !== "AbortError") console.warn("Unable to load donation options", error);
      }
    };

    loadDonationOptions();
    return () => controller.abort();
  }, [selectedCategory?.id, showProjectDropdown]);

  useEffect(() => {
    if (!showInitiativeDropdown) return undefined;

    const controller = new AbortController();

    const loadInitiatives = async () => {
      try {
        const response = await fetch(getApiUrl("/api/GetDonationCategoryList.ashx?typ=Donation&pageSize=50"), {
          headers: { Accept: "application/json" },
          credentials: "include",
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Initiative request failed with ${response.status}`);

        const result = await response.json();
        setInitiativeOptions((Array.isArray(result?.data) ? result.data : []).map((item, index) => mapDonationOption(item, index, language)).filter((option) => option.title));
      } catch (error) {
        if (error.name !== "AbortError") console.warn("Unable to load Tarahum initiatives", error);
      }
    };

    loadInitiatives();
    return () => controller.abort();
  }, [showInitiativeDropdown]);

  const buildDonation = () => ({
    id: selectedDropdownOption?.id || selectedInitiative?.id || `quick-${selectedCategory.id}`,
    catid: selectedDropdownOption?.catid || selectedInitiative?.id || selectedCategory.id || "0",
    subcatid: "0",
    projectid: selectedDropdownOption?.projectid || "0",
    title: selectedDropdownOption?.title || selectedInitiative?.title || selectedCategory.title,
    type: selectedCategory.type,
    typeLabel: selectedCategory.typeLabel || selectedCategory.title,
    caseNo: "",
    categoryTitle: selectedDropdownOption?.categoryTitle || selectedCategory.title,
    amount: String(donationAmount || ""),
  });

  const validateDonation = () => {
    if (!selectedCategory) {
      setValidationMessage(language === "ar" ? "لا توجد فئات تبرع متاحة حالياً." : "No donation categories are available right now.");
      return false;
    }

    if (!donationAmount || donationAmount <= 0) {
      setValidationMessage("Please choose or enter a valid donation amount.");
      return false;
    }

    if (showProjectDropdown && !selectedDropdownId) {
      setValidationMessage(`Please choose a ${selectedPosition === 4 ? "case" : "project"}.`);
      return false;
    }

    if (showInitiativeDropdown && !selectedInitiativeId) {
      setValidationMessage("Please choose an initiative.");
      return false;
    }

    setValidationMessage("");
    return true;
  };

  const handleCustomAmountChange = (event) => {
    const value = event.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(value);
    if (value) setSelectedAmount(0);
  };

  const handlePresetAmount = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
    setValidationMessage("");
  };

  const handleDonate = () => {
    if (!validateDonation()) return;
    interactions.onOpenDonationPopup(buildDonation());
  };

  const handleAddToCart = () => {
    if (!validateDonation()) return;

    const donation = buildDonation();
    const cartItem = {
      ...donation,
      quantity: 1,
      image: selectedCategory.image,
    };
    const existingItems = getStoredCartItems();
    localStorage.setItem(
      CART_STORAGE_KEY,
      JSON.stringify([cartItem, ...existingItems.filter((item) => item.id !== cartItem.id)])
    );
    navigateTo("/cart");
  };

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="apply-info quk-dnt">
        <div className="container">
          <div className="apply-info__wrap">
            <div className="apply-info__card">
              <div className="apply-info__left" style={leftPanelStyle}>
                <div className="apply-info__hero">
                  <div className="apply-info__badge mb-30">
                    <i className="fa-solid fa-shield-halved"></i>
                    {text.quickDonation}
                  </div>
                  <h1>{content.title}</h1>
                  <p>{content.subtitle}</p>
                </div>

                <div className="apply-info__status">
                  <div className="stats-box">
                    {content.stats.map((stat) => (
                      <div className="stat" key={`${stat.title}-${stat.subtitle}`}>
                        <h2>{stat.title}</h2>
                        <p>{stat.subtitle}</p>
                      </div>
                    ))}
                  </div>

                  <div className="dwn-w">
                    <h4>{content.bottomTitle}</h4>
                    <p>{content.bottomSubtitle}</p>
                  </div>
                </div>
              </div>

              <div className="apply-info__right">
                <div className="top-right">
                  <span>
                    <img src="/images/img/secure.svg" width="18px" alt="" />
                    {text.secure}
                  </span>
                  <span>
                    <i className="fa-solid fa-lock"></i>
                    {text.dataSafe}
                  </span>
                </div>

                <h2 className="quick-title">{text.quickDonation}</h2>
                <div className="section-title">{text.chooseWhere}</div>
                <div className="category-grid">
                  {donationCategories.map((category, index) => {
                    return <button
                      className={`card-option${String(selectedCategoryId) === String(category.id) ? " active" : ""}`}
                      type="button"
                      key={category.id}
                      onClick={() => {
                        setSelectedCategoryId(category.id);
                        setValidationMessage("");
                      }}
                    >
                      <div className="crd-imgs-op">
                        <img src={category.image} alt="" />
                      </div>
                      <div>
                        {category.title}
                        <small>{category.subtitle}</small>
                      </div>
                    </button>;
                  })}
                </div>

                {(showProjectDropdown || showInitiativeDropdown) ? (
                  <div className="quk-stlbn">
                    {showProjectDropdown ? (
                      <select
                        className="select-box"
                        value={selectedDropdownId}
                        onChange={(event) => {
                          setSelectedDropdownId(event.target.value);
                          setValidationMessage("");
                        }}
                      >
                        <option value="">{dropdownLabel}</option>
                        {dropdownOptions.map((option) => (
                          <option value={option.id} key={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {showInitiativeDropdown ? (
                      <select
                        className="select-box"
                        value={selectedInitiativeId}
                        onChange={(event) => {
                          setSelectedInitiativeId(event.target.value);
                          setValidationMessage("");
                        }}
                      >
                        <option value="">{text.selectInitiative}</option>
                        {initiativeOptions.map((option) => (
                          <option value={option.id} key={option.id}>
                            {option.title}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ) : null}

                <label className="project-amount-label quick-amount-label" htmlFor="quick-donation-amount">
                  {text.chooseAmount}
                </label>
                <div className="amount-grid">
                  {presetAmounts.map((amount) => (
                    <button
                      className={`card-option amount-card${!customAmount && selectedAmount === amount ? " active" : ""}`}
                      type="button"
                      key={amount}
                      onClick={() => handlePresetAmount(amount)}
                    >
                      <span>{text.currency}</span>
                      <strong>{formatAmount(amount)}</strong>
                    </button>
                  ))}
                  <div
                    className={`card-option amount-card${customAmount || selectedAmount === 0 ? " active" : ""}`}
                    onClick={() => {
                      setSelectedAmount(0);
                      setValidationMessage("");
                    }}
                  >
                    <span style={{ marginBottom: "5px" }}>{text.otherAmount}</span>
                    <strong style={{ fontSize: "20px" }}>
                      <input
                        id="quick-donation-amount"
                        type="text"
                        className="custom-input"
                        placeholder={text.currency}
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        onFocus={() => {
                          setSelectedAmount(0);
                          setValidationMessage("");
                        }}
                      />
                    </strong>
                  </div>
                </div>

                <div className="secure-note">
                  <i className="fa-solid fa-lock"></i>
                  {text.secureNote}
                </div>

                {validationMessage ? (
                  <div className="quick-donation-alert" role="alert">
                    <i className="fa-solid fa-circle-exclamation"></i>
                    {validationMessage}
                  </div>
                ) : null}

                <div className="project-donate-row quick-donate-row">
                  <button type="button" onClick={handleDonate}>
                    <span>
                      <i className="fa-solid fa-hands-holding-circle"></i>
                    </span>
                    {text.donateNow}
                  </button>
                  <button type="button" onClick={handleAddToCart}>
                    <span>
                      <i className="fa-solid fa-cart-shopping"></i>
                    </span>
                    {text.addToCart}
                  </button>
                </div>

                <SmsDonationOptions
                  className="project-sms-box quick-other-ways"
                  language={language}
                  title={text.otherWays}
                  currency={text.currency}
                  codeLabel={text.code}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {language === "ar" ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
